/**
 * MeetingTypeSelectionView: 商談形式（オンライン／訪問・対面）選択UI。
 * 2026-08-04対面商談対応（U12）BR-U6-12。design-mockups/pre-booking-form.html Case4/5準拠。
 *
 * `meetingModePolicy=HYBRID`の場合のみラジオボタン2択（常時両方表示）をレンダリングする。
 * `ONLINE_ONLY`/`ON_SITE_ONLY`の場合は選択UIを表示せず、固定値を返す。
 * 単一CV経路（preBookingFormUi.ts）・複数CV経路（multiCvAppointmentListUi.ts）の両方から
 * 共通利用する（既存のcalendarGridUi.ts/desiredDateTimeInputUi.tsと同じ共通モジュール方式）。
 */

import { createElement } from "../core/shadowDomUtil";
import type { MeetingType } from "../types";

export interface MeetingTypeSelectionResult {
  element: HTMLElement;
  /** 現在選択中の商談形式を返す（HYBRID以外の場合は固定値）。 */
  getSelectedMeetingType: () => MeetingType;
}

/**
 * 商談形式に応じた見出し文言を返す（BR-U6-12確定内容）。
 */
export function getMeetingTypeTitle(meetingType: MeetingType): string {
  return meetingType === "ON_SITE"
    ? "担当者との訪問・対面でのご商談をリクエストする"
    : "担当者とのオンライン商談をリクエストする";
}

/**
 * 商談形式選択UIを構築する。
 *
 * @param meetingModePolicy U3のMemberGroup.meetingModePolicy相当の値
 * @param onChange 選択が変化した際に呼ばれるコールバック（HYBRID時のみ発火。ONLINE_ONLY/ON_SITE_ONLYでは呼ばれない）
 */
export function renderMeetingTypeSelection(
  meetingModePolicy: "ONLINE_ONLY" | "ON_SITE_ONLY" | "HYBRID" | null | undefined,
  onChange: (meetingType: MeetingType) => void
): MeetingTypeSelectionResult {
  const wrapper = createElement("div");

  if (meetingModePolicy === "ONLINE_ONLY") {
    return { element: wrapper, getSelectedMeetingType: () => "ONLINE" };
  }
  if (meetingModePolicy === "ON_SITE_ONLY") {
    return { element: wrapper, getSelectedMeetingType: () => "ON_SITE" };
  }

  // HYBRID（または未指定時のフォールバック）: ラジオボタン2択、常時両方表示、既定はONLINE
  let selected: MeetingType = "ONLINE";

  const group = createElement("div", { className: "spica-meeting-type-group" });
  const onlineButton = createElement("button", {
    className: "spica-meeting-type-option selected",
    text: "オンライン",
  });
  const onsiteButton = createElement("button", {
    className: "spica-meeting-type-option",
    text: "訪問・対面",
  });

  const refresh = (): void => {
    onlineButton.classList.toggle("selected", selected === "ONLINE");
    onsiteButton.classList.toggle("selected", selected === "ON_SITE");
  };

  onlineButton.addEventListener("click", () => {
    if (selected === "ONLINE") return;
    selected = "ONLINE";
    refresh();
    onChange(selected);
  });
  onsiteButton.addEventListener("click", () => {
    if (selected === "ON_SITE") return;
    selected = "ON_SITE";
    refresh();
    onChange(selected);
  });

  group.appendChild(onlineButton);
  group.appendChild(onsiteButton);
  wrapper.appendChild(group);

  return { element: wrapper, getSelectedMeetingType: () => selected };
}
